import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CifradoAES, CifradoRSA } from "../../components/FuncionesCifrado";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { BASE_URL } from '../../config/api';

const SubirArchivos = ({ tokenSeguridad, ubicacion, idCarpeta , publicKeyRSA, activeTab, obtenerArchivos, sideBarBig, movil }) => {
    // State management
    const [fileList, setFileList] = useState([]);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [uploadStats, setUploadStats] = useState({
        uploaded: 0,
        total: 0,
        isUploading: false,
        currentProgress: 0,
        currentFileName: ''
    });
    const [concurrentUploads, setConcurrentUploads] = useState(0);
    const [showModal, setShowModal] =useState(false);
    const [modalType, setModalType] = useState('addFolder');
    const [nuevaCarpeta, setNuevaCrpeta] = useState('');
    
    // Refs for DOM elements
    const folderInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const uploadWindowRef = useRef(null);
    const uploadListRef = useRef(null);
    const newFolderInput = useRef(null);
    
    // Constants
    const MAX_CONCURRENT_UPLOADS = 5;
    const RETRY_ATTEMPTS = 3;
    const RETRY_DELAY = 3000;

    const aesEncryption = new CifradoAES();
    const rsaEncryption = new CifradoRSA();

    // Initialize folder input attributes
    useEffect(() => {
        if (folderInputRef.current) {
            const input = folderInputRef.current;
            input.setAttribute("webkitdirectory", "");
            input.setAttribute("mozdirectory", "");
            input.setAttribute("msdirectory", "");
            input.setAttribute("odirectory", "");
            input.setAttribute("directory", "");
        }
        if (showModal && modalType === 'addFolder') {
            setTimeout(() => {
                newFolderInput.current?.focus();
            }, 0);
        }
    }, [showModal, modalType]);

    // Start upload process when files are added
    useEffect(() => {
        if (uploadStats.total > 0 && !uploadStats.isUploading) {
            startUploadProcess();
        }
    }, [uploadStats.total]);

    // Utility functions
    const formatBytes = useCallback((bytes, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }, []);

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // File handling
    const handleFileSelection = useCallback(async (event) => {
        event.preventDefault();
        const files = event.target?.files || event;
        if (!files?.length) return; 

        const newFilesToUpload = [];
        const newFileList = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            // Create unique ID for each file
            //const fileId = `${file.lastModified}-${file.name}-${file.size}-${Date.now()}`;
            const uid = uuidv4();
            const fileId = `${file.lastModified}-${uid}-${Date.now()}`;
            const fullPath = file.webkitRelativePath || file.name;

            newFilesToUpload.push({ 
                file, 
                type: "Archivo",
                id: fileId,
                fullPath: fullPath
            });
            newFileList.push({
                id: fileId,
                size: file.size,
                name: file.name,
                type: "Archivo",
                status: 'pending' // pending, uploading, success, error
            });
        }

        //setFilesToUpload(prev => [...prev, ...newFilesToUpload]);
        setFilesToUpload(newFilesToUpload);
        setFileList(prev => [...prev, ...newFileList]);
        setUploadStats(prev => ({
            ...prev,
            total: prev.total + newFilesToUpload.length
        }));

        // Show upload window
        if (uploadWindowRef.current) {
            uploadWindowRef.current.className = "";
        }
        event.target.value = '';
    }, []);

    const handleFolderSelect = (event) => {
        const files = event.target.files;
        console.log(`Total files selected: ${files.length}`);
        
        if (!files || files.length === 0) {
            return;
        }

        const newFilesToUpload = [];
        const newFileList = [];
        
        // Process ALL files from the FileList
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const uid = uuidv4();
            const fileId = `${file.lastModified}-${uid}-${Date.now()}`;
            // Get the full relative path (includes folder structure)
            const fullPath = file.webkitRelativePath || file.name;
            
            console.log(`Processing: ${fullPath}`);

            newFilesToUpload.push({ 
                file,
                type: "Archivo",
                id: fileId,
                fullPath: fullPath
            });
            newFileList.push({
                id: fileId,
                size: file.size,
                name: file.name,
                type: "Archivo",
                status: 'pending' // pending, uploading, success, error
            });
            
            /* newFilesToUpload.push({
                id: fileId, // Unique ID
                file: file,
                name: file.name,
                fullPath: fullPath, // THIS IS CRITICAL - preserves folder structure
                size: file.size,
                type: file.type,
                status: 'pending'
            }); */
        }
               
        // Update your state with ALL files
        setFilesToUpload(newFilesToUpload);
        setFileList(prev => [...prev, ...newFileList]);
        setUploadStats(prev => ({
            ...prev,
            total: prev.total + newFilesToUpload.length
        }));

        // Show upload window
        if (uploadWindowRef.current) {
            uploadWindowRef.current.className = "";
        }
        event.target.value = '';
    };

    // Upload process management
    const startUploadProcess = useCallback(async () => {
        console.log('Starting upload process');
        setUploadStats(prev => ({ ...prev, isUploading: true }));
        
        let uploadedCount = 0;
        let completedUploads = 0; // Track completed uploads locally
        const totalFiles = filesToUpload.length;

        const handleUploadComplete = (fileId, success = true) => {
            setConcurrentUploads(prev => prev - 1);
            completedUploads++;
            
            setUploadStats(prev => ({
                ...prev,
                uploaded: completedUploads
            }));
            
            // Mark file status
            setFileList(prev => prev.map(f => 
                f.id === fileId ? { ...f, status: success ? 'success' : 'error' } : f
            ));            
            
            // Check if all uploads are complete
            if (completedUploads >= totalFiles) {
                //setFilesToUpload([]);
                setUploadStats(prev => ({
                    ...prev,
                    isUploading: false,
                    uploaded: totalFiles
                }));
                obtenerArchivos?.(activeTab);
                //alert("Archivos Subidos Con Éxito");
                setModalType('upload');
                setShowModal(true);
            }
        };

        while (uploadedCount < totalFiles) {
        if (concurrentUploads < MAX_CONCURRENT_UPLOADS) {
            const fileToUpload = filesToUpload[uploadedCount];
            setConcurrentUploads(prev => prev + 1);
            
            // Mark file as uploading
            setFileList(prev => prev.map(f => 
                f.id === fileToUpload.id ? { ...f, status: 'uploading' } : f
            ));
            
            // Don't await here to allow concurrent uploads
            uploadFile(fileToUpload.file, fileToUpload.id, fileToUpload.fullPath)
            .then(() => {
                handleUploadComplete(fileToUpload.id, true);
            })
            .catch(error => {
                console.error('Upload failed:', error);
                handleUploadComplete(fileToUpload.id, false);
            });
            
            uploadedCount++;
        } else {
            await sleep(200);
        }
        }
    }, [filesToUpload, concurrentUploads, uploadStats.uploaded, uploadStats.total, obtenerArchivos]);

    // Individual file upload
    const uploadFile = useCallback(async (file, fileId, fullPath) => {
        console.log('Uploading file:', file.name, 'with ID:', fileId);
        setUploadStats(prev => ({ ...prev, currentFileName: file.name }));

        const formData = new FormData();
        
        // Add form data
        /* const sessionData = getSessionData();
        const userData = {
        ...sessionData,
        t_seg: tokenSeguridad,
        ubicacion: ubicacion,
        lastModified: file.lastModified.toString()
        }; */
        const userData = {
            id_usuario: sessionStorage.getItem('id_usuario'),
            t_u: sessionStorage.getItem('token_nk107803'),
            tip_u: sessionStorage.getItem('tipo_usuario'),
            t_seg: tokenSeguridad,
            ubicacion: ubicacion,
            id_carpeta: idCarpeta,
            lastModified: file.lastModified.toString(),
            full_path: fullPath
        };
        console.log(userData);
        // Encrypt data
        for (const [key, value] of Object.entries(userData)) {
            formData.append(key, aesEncryption.cifrar(value).datos);
        }

        // Add encryption keys
        const encryptedKey = await rsaEncryption.cifrar(aesEncryption.llave, publicKeyRSA);
        formData.append("ivCifradoSimetrico", aesEncryption.iv);
        formData.append("keyCifradoSimetrico", encryptedKey);
        formData.append("archivo", file);

        let attempts = 0;
        
        while (attempts < RETRY_ATTEMPTS) {
        try {
            const response = await axios.post(`${BASE_URL}/cratos/ModuloDrive/PHP/Implementaciones/subirArchivos.php`,
            formData,
            {
                onUploadProgress: (progressEvent) => {
                const progress = Math.round((100 * progressEvent.loaded) / progressEvent.total);
                setUploadStats(prev => ({ ...prev, currentProgress: progress }));
                },
                maxRedirects: 0,
            }
            );

            if (response.status === 200 && response.statusText === 'OK') {
            const data = response.data;
            if (parseInt(data) !== 0) {
                return Promise.resolve();
            }
            }
            
            throw new Error(`Upload failed with status: ${response.status}`);
            
        } catch (error) {
            attempts++;
            console.error(`Upload attempt ${attempts} failed:`, error);
            
            if (attempts >= RETRY_ATTEMPTS) {
            throw error;
            }
            
            if (error.code === "ERR_NETWORK") {
            await sleep(RETRY_DELAY * attempts); // Exponential backoff
            }
        }
        }
    }, [tokenSeguridad, ubicacion, idCarpeta, publicKeyRSA]);

    // Retry file upload
    const retryFailedUploads = useCallback(() => {
        const failedFiles = fileList.filter(f => f.status === 'error');
        
        if (failedFiles.length === 0) return;
        
        // Find the original file objects
        const filesToRetry = failedFiles.map(failedFile => {
            return filesToUpload.find(f => f.id === failedFile.id);
        }).filter(Boolean);
        
        // Reset their status to pending
        setFileList(prev => prev.map(f => 
            f.status === 'error' ? { ...f, status: 'pending' } : f
        ));
        
        // Replace upload queue with only failed files
        setFilesToUpload(filesToRetry);
        // Reset upload stats for retry
        setUploadStats(prev => ({
            uploaded: prev.uploaded - failedFiles.length,
            total: prev.total,
            isUploading: false,
            currentProgress: 0,
            currentFileName: ''
        }));
        setTimeout(() => {
            startUploadProcess();
        }, 100);
        
    }, [fileList, filesToUpload]);

    // Add a "Clear All" button to clean up everything
    const clearCompletedUploads = useCallback(() => {
        // Remove successful uploads
        const remainingFiles = fileList.filter(f => f.status === 'error' || f.status === 'pending');
        
        if (remainingFiles.length === 0) {
            // Everything is done, clear everything
            setFileList([]);
            setFilesToUpload([]);
            setUploadStats({
                uploaded: 0,
                total: 0,
                isUploading: false,
                currentProgress: 0,
                currentFileName: ''
            });
            hideUploadWindow({ preventDefault: () => {} });
        } else {
            // Keep only failed/pending files
            setFileList(remainingFiles);
            const remainingToUpload = filesToUpload.filter(f => 
                remainingFiles.some(rf => rf.id === f.id)
            );
            setFilesToUpload(remainingToUpload);
            setUploadStats(prev => ({
                ...prev,
                uploaded: 0,
                total: remainingFiles.length
            }));
        }
    }, [fileList, filesToUpload]);
    // UI event handlers
    const handleFolderClick = useCallback((e) => {
        e.preventDefault();
        folderInputRef.current?.click();
    }, []);

    const handleFileClick = useCallback((e) => {
        e.preventDefault();
        fileInputRef.current?.click();
    }, []);

    const toggleUploadList = useCallback((show) => {
        if (uploadListRef.current) {
            uploadListRef.current.className = show ? "card-body p-0" : "d-none";
        }
    }, []);

    const hideUploadWindow = useCallback((e) => {
        e.preventDefault();
        // Everything is done, clear everything
        setFileList([]);
        setFilesToUpload([]);
        setUploadStats({
            uploaded: 0,
            total: 0,
            isUploading: false,
            currentProgress: 0,
            currentFileName: ''
        });
        if (uploadWindowRef.current) {
            uploadWindowRef.current.className = "d-none";
        }
    }, []);

    const crearCarpeta = async() =>{
        const formData = new FormData();
        const userData = {
            id_usuario: sessionStorage.getItem('id_usuario'),
            t_u: sessionStorage.getItem('token_nk107803'),
            tip_u: sessionStorage.getItem('tipo_usuario'),
            t_seg: tokenSeguridad,
            ubicacion: ubicacion,
            id_carpeta: idCarpeta,
            nombreCarpeta: nuevaCarpeta
        };

        // Encrypt data
        for (const [key, value] of Object.entries(userData)) {
            formData.append(key, aesEncryption.cifrar(value).datos);
        }

        // Add encryption keys
        const encryptedKey = await rsaEncryption.cifrar(aesEncryption.llave, publicKeyRSA);
        formData.append("ivCifradoSimetrico", aesEncryption.iv);
        formData.append("keyCifradoSimetrico", encryptedKey);

        try {
            const response = await axios.post(`${BASE_URL}/cratos/ModuloDrive/PHP/Implementaciones/crearCarpeta.php`,
                formData,
                {
                    maxRedirects: 0,
                }
            );

            if (response.status === 200 && response.statusText === 'OK') {
                const data = response.data;
                if (parseInt(data) === 1) {
                    obtenerArchivos?.(activeTab);
                    setShowModal(false);
                    return;
                    //return Promise.resolve();
                }                
            }
            
            throw new Error(`Fallo crear carpeta, status: ${response.status}`);
            
        } catch (error) {
            
            console.error(`Crear carpeta, error:`, error);
/*             if (error.code === "ERR_NETWORK") {
            await sleep(RETRY_DELAY * attempts); // Exponential backoff
            } */
        }
    }
    return (
        <>
        {/* Upload Controls */}
            <button className={`btn btn-outline-light btn-lg btn-cargar text-light dropdown-toggle ${movil === true ? 'rounded-circle ms-2' : ''}`} type="button" data-bs-toggle="dropdown" aria-expanded="false">
                {sideBarBig ? '+ Crear o cargar':'+'}
            </button>
            <div className="dropdown">
                <ul className="dropdown-menu">
                    <li>
                        <a className="dropdown-item" href="#" onClick={handleFileClick}>
                            Cargar archivo
                        </a>
                    </li>
                    <li>
                        <a className="dropdown-item" href="#" onClick={handleFolderClick}>
                            Cargar carpeta
                        </a>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                        <a className="dropdown-item" href="#" onClick={() => setShowModal(true)}>
                            Crear carpeta
                        </a>
                    </li>
                </ul>
            </div>

        {/* Hidden File Inputs */}
        <div>
            <input
            ref={folderInputRef}
            className="visually-hidden"
            id="inputCarpeta"
            type="file"
            onChange={handleFolderSelect}
            multiple
            />
            <input
            ref={fileInputRef}
            className="visually-hidden"
            id="inputArchivo"
            type="file"
            onChange={handleFileSelection}
            multiple
            />
        </div>

        {/* Upload Progress Window */}
        <div
            ref={uploadWindowRef}
            className="d-none"
            style={{
            bottom: "-1px",
            right: "0px",
            position: "fixed",
            zIndex: "3",
            width: "50vw",
            maxHeight: "45vh"
            }}
        >
            <div className="card">
            <div className="card-header p-0 bg-secondary">
                <span className="text-white ps-2 py-1 float-start">
                {uploadStats.uploaded} archivos de {uploadStats.total}
                {uploadStats.isUploading && (
                    <>
                    {` ${uploadStats.currentProgress}%`}
                    <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                    </>
                )}
                </span>
                <div className="btn-group float-end" role="group">
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => toggleUploadList(false)}
                >
                    <i className="bi bi-dash"></i>
                </button>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => toggleUploadList(true)}
                >
                    <i className="bi bi-square"></i>
                </button>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={hideUploadWindow}
                >
                    <i className="bi bi-x"></i>
                </button>
                </div>
            </div>
            
            <div ref={uploadListRef} className="card-body p-0">
                <ul className="list-group list-group-flush">
                {fileList.map((file) => (
                    <li
                    key={file.id}
                    className="list-group-item d-flex justify-content-between align-items-center"
                    >
                    <span className="text-break">{file.name}</span>
                    <div className="d-flex align-items-center">
                        <span className="badge text-secondary me-2">{formatBytes(file.size)}</span>
                        <span className={`badge ${
                        file.status === 'pending' ? 'text-secondary' :
                        file.status === 'uploading' ? 'text-warning' :
                        file.status === 'success' ? 'text-success' :
                        'text-danger'
                        }`}>
                        {file.status === 'pending' && '⏳'}
                        {file.status === 'uploading' && '📤'}
                        {file.status === 'success' && '✅'}
                        {file.status === 'error' && '❌'}
                        </span>
                        {file.status === 'uploading' && (
                        <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true"></span>
                        )}
                    </div>
                    </li>
                ))}
                {uploadStats.currentFileName && (
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                    <span className="text-break">{uploadStats.currentFileName}</span>
                    <div className="d-flex align-items-center">
                        <span className="badge text-success me-2">
                        {uploadStats.currentProgress}%
                        </span>
                        {uploadStats.isUploading && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        )}
                    </div>
                    </li>
                )}
                </ul>
            </div>
            <div className="card-footer p-2 d-flex justify-content-between">
                <div>
                    {fileList.some(f => f.status === 'error') && (
                        <button 
                            className="btn btn-sm btn-warning"
                            onClick={retryFailedUploads}
                            disabled={uploadStats.isUploading}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Reintentar fallidos
                        </button>
                    )}
                </div>
                <div>
                    {!uploadStats.isUploading && (
                        <button 
                            className="btn btn-sm btn-secondary"
                            onClick={clearCompletedUploads}
                        >
                            <i className="bi bi-check2-all me-1"></i>
                            Limpiar completados
                        </button>
                    )}
                </div>
            </div>
            </div>
        </div>

        {/*Modal Nueva Carpeta*/}
        {showModal && (
            <div className="modal show d-block" id="modalSubirArchivos" tabIndex="-1" data-bs-backdrop="static">
                <div className="modal-backdrop show z-n1"></div>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {modalType === 'addFolder' && 'Crear Carpeta'}
                                {modalType === 'upload' && 'Carga de archivos'}
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close"
                                onClick={() => setShowModal(false)}
                            ></button>
                        </div>
                        <div className="modal-body">
                            {(modalType === 'addFolder') && (
                                <div className='row'>
                                    <div className='col-12'>
                                        <div className="input-group mb-3">
                                            <span className="input-group-text">Nombre</span>
                                            <span className="input-group-text">{ubicacion}</span>
                                            <input ref={newFolderInput} type="text" className="form-control" id="nuevaCarpeta" value={nuevaCarpeta}
                                                onChange={(e) => {
                                                    const nombre = e.currentTarget.value;
                                                    setNuevaCrpeta(nombre);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {(modalType === 'upload') && (
                                <div className='row'>
                                    <div className='col-12'>
                                        <span className='fs-3'>Archivos procesados correctamente.</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            
                            {modalType === 'addFolder' && (<>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={() => crearCarpeta()}>Crear Carpeta</button>
                            </>)}
                            {modalType === 'upload' && (<button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>Aceptar</button>)}
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default SubirArchivos;